import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import JSZip from "jszip";
import api from "../../../../services/api";
import ImageUpload from "../../../../components/image-upload/image-upload";
import VariantButton from "../../../../components/variant-button/variant-button";

const IMAGE_MIME = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
};
const INPUT_CLASS = "w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500";

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (char) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[char]);
}

// Supports both data.json + images/ and a ZIP containing one parent folder.
async function readBlogZip(file) {
  if (!file || !/\.zip$/i.test(file.name)) {
    throw new Error("Vui lòng chọn một file .zip.");
  }
  const zip = await JSZip.loadAsync(file);
  const manifests = Object.values(zip.files).filter(
    (entry) => !entry.dir && /(^|\/)data\.json$/i.test(entry.name) &&
      !entry.name.startsWith("__MACOSX/"),
  );
  if (manifests.length !== 1) {
    throw new Error("ZIP phải chứa đúng một file data.json.");
  }

  const manifest = manifests[0];
  const directory = manifest.name.slice(0, manifest.name.lastIndexOf("/") + 1);
  let items;
  try {
    items = JSON.parse((await manifest.async("string")).replace(/^\uFEFF/, ""));
  } catch {
    throw new Error("data.json không phải JSON hợp lệ.");
  }
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error('data.json phải là một mảng [{ "name": "...", "image": "..." }].');
  }

  const entries = items.map((item, index) => {
    if (!item || typeof item.name !== "string" || !item.name.trim() ||
        typeof item.image !== "string" || !item.image.trim()) {
      throw new Error(`Mục số ${index + 1} thiếu name hoặc image.`);
    }
    const image = item.image.trim().replace(/\\/g, "/").replace(/^(\.\/)+/g, "");
    const parts = image.split("/");
    if (image.startsWith("/") || parts.includes("..") || parts.includes("") ||
        /^[a-z]+:/i.test(image)) {
      throw new Error(`Đường dẫn ảnh không hợp lệ ở mục ${index + 1}.`);
    }

    const ext = image.split(".").pop().toLowerCase();
    if (!IMAGE_MIME[ext]) {
      throw new Error(`Ảnh ${image} không thuộc định dạng JPG, PNG, WEBP hoặc GIF.`);
    }
    const relativePath = image.startsWith("images/") ? image : `images/${image}`;
    const imagePath = directory + relativePath;
    const imageEntry = zip.file(imagePath);
    if (!imageEntry || imageEntry.dir) {
      throw new Error(`Không tìm thấy ${imagePath} trong ZIP (mục ${index + 1}).`);
    }
    return {
      name: item.name.trim(),
      imagePath,
      imageEntry,
      filename: image.split("/").pop(),
      mime: IMAGE_MIME[ext],
    };
  });

  return { entries };
}

function makeHtml(entries, imageUrls) {
  return entries.map(({ name, imagePath }) => {
    const safeName = escapeHtml(name);
    const safeUrl = escapeHtml(imageUrls.get(imagePath));
    return `<h2>${safeName}</h2>\n<figure><img src="${safeUrl}" alt="${safeName}" loading="lazy" /><figcaption>${safeName}</figcaption></figure>`;
  }).join("\n");
}

export default function AddBlogFromZip() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const uploadedUrlsRef = useRef(new Map());
  const [permissions, setPermissions] = useState(null);
  const [blogTypes, setBlogTypes] = useState([]);
  const [form, setForm] = useState({
    title: "",
    slug: "",
    locale: "",
    image_url: "",
    blog_type_id: "",
    author: "",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [packageData, setPackageData] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const [permissionsResult, typesResult] = await Promise.all([
          api.get("/users/permissions"),
          api.get("/blogTypes"),
        ]);
        setPermissions(permissionsResult.data.data || []);
        setBlogTypes(typesResult.data.data || []);
      } catch {
        setError("Không thể tải quyền hoặc danh sách loại blog.");
        setPermissions([]);
      }
    }
    load();
  }, []);

  const selectZip = async (file) => {
    if (!file || submitting) return;
    setError("");
    setStatus("Đang đọc file ZIP...");
    setSelectedFile(null);
    setPackageData(null);
    uploadedUrlsRef.current = new Map();
    setUploadedCount(0);
    try {
      const parsed = await readBlogZip(file);
      setSelectedFile(file);
      setPackageData(parsed);
      setStatus("");
    } catch (err) {
      setError(err.message || "Không thể đọc ZIP.");
      setStatus("");
    }
  };

  const uploadImages = async (entries) => {
    const uniqueEntries = [...new Map(entries.map((entry) => [entry.imagePath, entry])).values()];
    const urls = uploadedUrlsRef.current;
    setUploadedCount(urls.size);
    const pending = uniqueEntries.filter((entry) => !urls.has(entry.imagePath));
    if (!pending.length) return urls;

    const pocketBase = (process.env.REACT_APP_POCKETBASE || "").replace(/\/$/, "");
    if (!pocketBase) {
      throw new Error("Chưa cấu hình REACT_APP_POCKETBASE.");
    }
    setStatus("Đang kết nối PocketBase...");
    const authResponse = await axios.post(
      `${pocketBase}/api/collections/_superusers/auth-with-password`,
      {
        identity: process.env.REACT_APP_IDENTITY,
        password: process.env.REACT_APP_PASSWORD,
      },
    );
    const token = authResponse.data.token;
    if (!token) throw new Error("PocketBase không trả về token.");

    setStatus("Đang upload hình ảnh...");
    // Keep a small concurrency to avoid overloading PocketBase with large ZIPs.
    for (let start = 0; start < pending.length; start += 3) {
      await Promise.all(pending.slice(start, start + 3).map(async (entry) => {
        const blob = await entry.imageEntry.async("blob");
        if (blob.size > 5 * 1024 * 1024) {
          throw new Error(`Ảnh ${entry.filename} vượt quá 5MB.`);
        }
        const imageFile = new File([blob], entry.filename, { type: entry.mime });
        const body = new FormData();
        body.append("image", imageFile);
        const response = await axios.post(
          `${pocketBase}/api/collections/blog_images/records`,
          body,
          { headers: { Authorization: token } },
        );
        const { id, collectionName, image } = response.data;
        if (!id || !collectionName || !image) {
          throw new Error(`PocketBase không trả về URL cho ${entry.filename}.`);
        }
        const imageUrl = `${pocketBase}/api/files/${collectionName}/${id}/${encodeURIComponent(image)}`;
        await api.post("/blogs/images", { image_url: imageUrl });
        urls.set(entry.imagePath, imageUrl);
        setUploadedCount(urls.size);
      }));
    }
    return urls;
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!packageData || !form.title.trim() || !form.slug.trim() ||
        !form.locale || !form.blog_type_id || !form.author.trim()) {
      setError("Vui lòng nhập tiêu đề, slug, ngôn ngữ, loại blog, tác giả và chọn ZIP hợp lệ.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const imageUrls = await uploadImages(packageData.entries);
      const content = makeHtml(packageData.entries, imageUrls);
      const image_url = form.image_url.trim() ||
        imageUrls.get(packageData.entries[0].imagePath);

      setStatus("Đang tạo bài blog...");
      await api.post("/blogs", {
        title: form.title.trim(),
        slug: form.slug.trim(),
        locale: form.locale,
        image_url,
        blog_type_id: form.blog_type_id,
        author: form.author.trim(),
        content,
      });
      navigate("/blogs");
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Không thể tạo blog.");
      setStatus("");
    } finally {
      setSubmitting(false);
    }
  };

  const setField = (name, value) => {
    setForm((previous) => ({ ...previous, [name]: value }));
  };
  const totalImages = packageData
    ? new Set(packageData.entries.map((entry) => entry.imagePath)).size
    : 0;

  if (permissions && !permissions.includes("blogs.create")) return null;
  if (!permissions) return null;

  return (
    <div className="flex-1 overflow-y-auto p-6 bg-gray-100 min-h-screen">
      <div className="w-full mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Quản lý Blog</h1>
        <div className="bg-white shadow-xl rounded-lg p-6">
          <h2 className="text-2xl font-semibold text-gray-700 mb-4">Thêm Blog từ ZIP</h2>
          {error && (
            <p role="alert" className="bg-red-100 text-red-700 border border-red-300 p-3 mb-4 rounded-md">
              {error}
            </p>
          )}
          <ImageUpload />
          <div className="space-y-4">
            <input
              type="text"
              placeholder="Tiêu đề"
              value={form.title}
              onChange={(event) => setField("title", event.target.value)}
              className={INPUT_CLASS}
              disabled={submitting}
            />
            <input
              type="text"
              placeholder="Slug (ví dụ: mau-nail-co-dau)"
              value={form.slug}
              onChange={(event) => setField("slug", event.target.value)}
              className={INPUT_CLASS}
              disabled={submitting}
            />
            <input
              type="text"
              placeholder="URL hình ảnh (để trống sẽ lấy ảnh đầu tiên trong ZIP)"
              value={form.image_url}
              onChange={(event) => setField("image_url", event.target.value)}
              className={INPUT_CLASS}
              disabled={submitting}
            />
            <select
              value={form.locale}
              onChange={(event) => setField("locale", event.target.value)}
              className={INPUT_CLASS}
              disabled={submitting}
            >
              <option value="">Chọn ngôn ngữ</option>
              <option value="vi">Tiếng Việt (vi)</option>
              <option value="en">English (en)</option>
              <option value="ja">日本語 (ja)</option>
              <option value="zh-Hans">简体中文 (zh-Hans)</option>
              <option value="zh-Hant">繁體中文 (zh-Hant)</option>
            </select>
            <select
              value={form.blog_type_id}
              onChange={(event) => setField("blog_type_id", event.target.value)}
              className={INPUT_CLASS}
              disabled={submitting}
            >
              <option value="">Chọn loại blog</option>
              {blogTypes.map((type) => (
                <option value={type.id} key={type.id}>{type.name}</option>
              ))}
            </select>

            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Nội dung từ file ZIP</p>
              <input
                ref={inputRef}
                type="file"
                accept=".zip,application/zip,application/x-zip-compressed"
                className="hidden"
                disabled={submitting}
                onChange={(event) => {
                  selectZip(event.target.files?.[0]);
                  event.target.value = "";
                }}
              />
              <div
                role="button"
                tabIndex={submitting ? -1 : 0}
                onClick={() => !submitting && inputRef.current?.click()}
                onKeyDown={(event) => {
                  if (!submitting && (event.key === "Enter" || event.key === " ")) {
                    event.preventDefault();
                    inputRef.current?.click();
                  }
                }}
                onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
                onDragLeave={(event) => { event.preventDefault(); setDragging(false); }}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragging(false);
                  if (!submitting) selectZip(event.dataTransfer.files?.[0]);
                }}
                className={`border-2 border-dashed rounded-lg px-6 py-10 text-center transition-colors ${dragging ? "border-[#34699A] bg-blue-50" : "border-gray-300 bg-gray-50"} ${submitting ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:border-[#34699A]"}`}
              >
                <p className="font-semibold text-gray-700">
                  {selectedFile ? selectedFile.name : "Kéo thả ZIP vào đây hoặc bấm để chọn file"}
                </p>
                <p className="mt-2 text-sm text-gray-500">ZIP chứa data.json và thư mục images/</p>
              </div>
              {packageData && (
                <div className="mt-3 p-4 border border-gray-200 rounded-lg">
                  <p className="font-semibold text-gray-800">
                    Đã đọc {packageData.entries.length} mẫu nail và {totalImages} ảnh
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Tên mẫu được lấy nguyên từ data.json. Không tự dịch. Ảnh được upload khi bấm Tạo.
                  </p>
                  <ol className="list-decimal pl-5 mt-3 max-h-48 overflow-y-auto text-sm text-gray-700 space-y-1">
                    {packageData.entries.map((entry, index) => (
                      <li key={index}>{entry.name}</li>
                    ))}
                  </ol>
                </div>
              )}
              {status && <p role="status" className="text-sm text-blue-700 mt-2">
                {status}{submitting && totalImages > 0 ? ` (${uploadedCount}/${totalImages} ảnh)` : ""}
              </p>}
            </div>

            <input
              type="text"
              placeholder="Người viết bài"
              value={form.author}
              onChange={(event) => setField("author", event.target.value)}
              className={INPUT_CLASS}
              disabled={submitting}
            />

            <div className="flex space-x-3 justify-end">
              <div className="w-[120px]">
                <VariantButton props={{
                  onClick: handleSubmit,
                  title: submitting ? "Đang tạo..." : "Tạo",
                  disabled: submitting,
                }} />
              </div>
              <div className="w-[120px]">
                <Link to="/blogs">
                  <VariantButton props={{ title: "Trở lại", onClick: null }} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
